// @ts-nocheck
/* eslint-disable */

import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
} from '@mui/material';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

export const SPECIAL_CHARS = [
  { key: 'hyphen',     char: '-', label: '- (Hyphen)' },
  { key: 'underscore', char: '_', label: '_ (Underscore)' },
  { key: 'slash',      char: '/', label: '/ (Slash)' },
  { key: 'dot',        char: '.', label: '. (Dot)' },
  { key: 'space',      char: ' ', label: 'Space' },
  { key: 'hash',       char: '#', label: '# (Hash)' },
  { key: 'at',         char: '@', label: '@ (At)' },
  { key: 'colon',      char: ':', label: ': (Colon)' },
  { key: 'comma',      char: ',', label: ', (Comma)' },
];

// ─────────────────────────────────────────────
// Helpers (used by builder, view, and eligibility modal)
// ─────────────────────────────────────────────

/** Build a validation_rules object from flat vr_* formValues */
export const buildValidationRules = (formValues) => {
  if (!formValues.vr_input_type) return null;
  const rules: any = { input_type: formValues.vr_input_type };
  const minLen = parseInt(formValues.vr_min_length, 10);
  const maxLen = parseInt(formValues.vr_max_length, 10);
  if (!isNaN(minLen) && minLen > 0) rules.min_length = minLen;
  if (!isNaN(maxLen) && maxLen > 0) rules.max_length = maxLen;
  rules.allow_special_chars = !!formValues.vr_allow_special_chars;
  if (rules.allow_special_chars) {
    rules.special_chars = SPECIAL_CHARS.filter(sc => formValues[`vr_char_${sc.key}`]).map(sc => sc.char);
  }
  return rules;
};

/** Populate flat vr_* keys from a validation_rules object */
export const initRulesIntoFormValues = (rules, setFormValues) => {
  const updates: any = {
    vr_input_type: rules?.input_type || '',
    vr_min_length: rules?.min_length != null ? String(rules.min_length) : '',
    vr_max_length: rules?.max_length != null ? String(rules.max_length) : '',
    vr_allow_special_chars: !!rules?.allow_special_chars,
  };
  SPECIAL_CHARS.forEach(sc => {
    updates[`vr_char_${sc.key}`] = !!(rules?.special_chars && rules.special_chars.includes(sc.char));
  });
  setFormValues(prev => ({ ...prev, ...updates }));
};

/** Generate a sample valid value so users can see immediately what the rule expects */
export const generateExample = (rules) => {
  if (!rules || !rules.input_type) return null;
  const { input_type, min_length, allow_special_chars, special_chars } = rules;
  const targetLen = min_length ? Math.max(min_length, 4) : 6;

  let base = '';
  if (input_type === 'numeric') {
    base = '1234567890'.repeat(4).slice(0, targetLen);
  } else if (input_type === 'alpha') {
    base = 'ABCDEFGHIJ'.repeat(3).slice(0, targetLen);
  } else {
    base = 'ABC12DEFGH'.repeat(3).slice(0, targetLen);
  }

  if (allow_special_chars && special_chars?.length > 0) {
    const mid = Math.floor(base.length / 2);
    base = base.slice(0, mid) + special_chars[0] + base.slice(mid);
  }
  return base;
};

/** One-line plain-text description of rules (for preview caption) */
export const describeRules = (rules, lang = 'en') => {
  if (!rules) return '';
  const isAr = lang === 'ar' || lang === 'ar-eg';
  const parts: string[] = [];

  if (rules.input_type) {
    const typeMap = {
      numeric:      isAr ? 'أرقام فقط'       : 'numbers only',
      alpha:        isAr ? 'أحرف فقط'         : 'letters only',
      alphanumeric: isAr ? 'أحرف وأرقام (كلاهما مطلوب)' : 'letters & numbers (both required)',
    };
    parts.push(typeMap[rules.input_type] || rules.input_type);
  }
  if (rules.min_length) parts.push((isAr ? 'أدنى ' : 'min ') + rules.min_length);
  if (rules.max_length) parts.push((isAr ? 'أقصى ' : 'max ') + rules.max_length);
  if (rules.allow_special_chars && rules.special_chars?.length) {
    parts.push((isAr ? 'أحرف خاصة: ' : 'special chars: ') + rules.special_chars.join(' '));
  }
  return parts.join(' · ');
};

// ─────────────────────────────────────────────
// ValidationRulesDisplay  (read-only, used in Info page)
// ─────────────────────────────────────────────

export const ValidationRulesDisplay = ({ rules, lang = 'en' }) => {
  const isAr = lang === 'ar-eg' || lang === 'ar';

  if (!rules || !rules.input_type) {
    return <span style={{ color: '#aaa', fontSize: 13 }}>{isAr ? 'لا توجد قواعد' : 'No rules defined'}</span>;
  }

  const typeLabel = {
    numeric:      isAr ? 'أرقام فقط'      : 'Numbers only',
    alpha:        isAr ? 'أحرف فقط'        : 'Letters only',
    alphanumeric: isAr ? 'أحرف وأرقام (كلاهما مطلوب)' : 'Letters & numbers (both required)',
  }[rules.input_type] || rules.input_type;

  const example = generateExample(rules);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
      <span style={{ fontSize: 13 }}>
        <b>{isAr ? 'النوع: ' : 'Type: '}</b>{typeLabel}
      </span>
      {rules.min_length != null && (
        <span style={{ fontSize: 13 }}>
          <b>{isAr ? 'الحد الأدنى: ' : 'Min length: '}</b>{rules.min_length}
        </span>
      )}
      {rules.max_length != null && (
        <span style={{ fontSize: 13 }}>
          <b>{isAr ? 'الحد الأقصى: ' : 'Max length: '}</b>{rules.max_length}
        </span>
      )}
      {rules.allow_special_chars && rules.special_chars?.length > 0 && (
        <span style={{ fontSize: 13 }}>
          <b>{isAr ? 'أحرف خاصة مسموحة: ' : 'Allowed special chars: '}</b>
          <code style={{ backgroundColor: '#f5f5f5', padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace' }}>
            {rules.special_chars.join('  ')}
          </code>
        </span>
      )}
      {example && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: 12, color: '#888' }}>{isAr ? 'مثال صحيح:' : 'Valid example:'}</span>
          <code style={{ fontSize: 13, backgroundColor: '#e8f4fd', color: '#1890ff', padding: '2px 10px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 'bold', border: '1px solid #91caff' }}>
            {example}
          </code>
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────
// ValidationRulesBuilder  (interactive builder for add/edit)
// ─────────────────────────────────────────────

interface Props {
  formValues: any;
  setFormValues: (updater: any) => void;
  initialRules?: any;
}

const ValidationRulesBuilder = ({ formValues, setFormValues, initialRules }: Props) => {
  const intl = useIntl();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initialRules && !initializedRef.current) {
      initializedRef.current = true;
      initRulesIntoFormValues(initialRules, setFormValues);
    }
  }, [initialRules]);

  const set = (key, value) => setFormValues(prev => ({ ...prev, [key]: value }));

  const inputType = formValues.vr_input_type || '';
  const allowSpecial = !!formValues.vr_allow_special_chars;

  const handleClear = () => {
    initializedRef.current = false;
    const cleared: any = { vr_input_type: '', vr_min_length: '', vr_max_length: '', vr_allow_special_chars: false };
    SPECIAL_CHARS.forEach(sc => { cleared[`vr_char_${sc.key}`] = false; });
    setFormValues(prev => ({ ...prev, ...cleared }));
  };

  // Live preview from current state
  const liveRules = buildValidationRules(formValues);
  const liveExample = generateExample(liveRules);
  const liveDesc = describeRules(liveRules);

  return (
    <Box sx={{ mt: 3, mb: 2, p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa', maxWidth: 680 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 'bold', color: '#252733', fontSize: '14px' }}>
            {intl.formatMessage({ id: 'IdentifierValidationRules' })} ({intl.formatMessage({ id: 'optional' }) || 'Optional'})
          </Typography>
          <Tooltip title={intl.formatMessage({ id: 'ValidationRulesHint' })} arrow placement="top">
            <span style={{ fontSize: '11px', color: '#1890ff', border: '1px solid #1890ff', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', cursor: 'help', lineHeight: 1, flexShrink: 0 }}>?</span>
          </Tooltip>
        </Box>
        {inputType && (
          <button
            type="button"
            onClick={handleClear}
            style={{ fontSize: '11px', color: '#e53935', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, textDecoration: 'underline' }}
          >
            {intl.formatMessage({ id: 'ClearValidationRules' })}
          </button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Input Type pill buttons */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#555', mb: 0.5 }}>
          {intl.formatMessage({ id: 'InputType' })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { value: '',             labelId: 'NoRestriction' },
            { value: 'numeric',      labelId: 'NumericOnly' },
            { value: 'alpha',        labelId: 'AlphaOnly' },
            { value: 'alphanumeric', labelId: 'AlphanumericOnly' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('vr_input_type', opt.value)}
              style={{
                padding: '4px 14px', borderRadius: 20, border: '1px solid',
                borderColor: inputType === opt.value ? '#363740' : '#ccc',
                backgroundColor: inputType === opt.value ? '#363740' : '#fff',
                color: inputType === opt.value ? '#fff' : '#555',
                fontSize: '12px', cursor: 'pointer',
              }}
            >
              {intl.formatMessage({ id: opt.labelId })}
            </button>
          ))}
        </Box>
      </Box>

      {/* Min / Max Length */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <label className="labelStyle" style={{ fontSize: '13px' }}>
            {intl.formatMessage({ id: 'MinLength' })}
          </label>
          <input
            type="number" min={1} max={999} className="inputStyle" style={{ width: 120 }}
            value={formValues.vr_min_length || ''}
            onChange={e => set('vr_min_length', e.target.value)}
            placeholder="e.g. 3"
          />
        </Box>
        <Box>
          <label className="labelStyle" style={{ fontSize: '13px' }}>
            {intl.formatMessage({ id: 'MaxLength' })}
          </label>
          <input
            type="number" min={1} max={9999} className="inputStyle" style={{ width: 120 }}
            value={formValues.vr_max_length || ''}
            onChange={e => set('vr_max_length', e.target.value)}
            placeholder="e.g. 20"
          />
        </Box>
      </Box>

      {/* Allow Special Chars */}
      <FormControlLabel
        control={
          <Checkbox
            size="small" checked={allowSpecial}
            onChange={e => set('vr_allow_special_chars', e.target.checked)}
            sx={{ color: '#363740', '&.Mui-checked': { color: '#363740' } }}
          />
        }
        label={<span style={{ fontSize: '13px' }}>{intl.formatMessage({ id: 'AllowSpecialChars' })}</span>}
        sx={{ mb: allowSpecial ? 1 : 0 }}
      />

      {/* Special Chars multi-select */}
      {allowSpecial && (
        <Box sx={{ pl: 1, pt: 0.5 }}>
          <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>
            {intl.formatMessage({ id: 'SpecialCharsAllowed' })}:
          </Typography>
          <FormGroup>
            <Grid container>
              {SPECIAL_CHARS.map(sc => (
                <Grid item xs={6} sm={4} key={sc.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!formValues[`vr_char_${sc.key}`]}
                        onChange={e => set(`vr_char_${sc.key}`, e.target.checked)}
                        sx={{ color: '#363740', '&.Mui-checked': { color: '#363740' } }}
                      />
                    }
                    label={<span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{sc.label}</span>}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </Box>
      )}

      {/* Live preview — only shown when a type is selected */}
      {inputType && (
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e8f4fd', border: '1px solid #91caff', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: '#888', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {intl.formatMessage({ id: 'RemarksValidationRules' })} — Preview
          </Typography>
          {liveDesc && (
            <Typography sx={{ fontSize: '12px', color: '#555', mb: 0.5 }}>{liveDesc}</Typography>
          )}
          {liveExample && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography sx={{ fontSize: '12px', color: '#888' }}>
                {intl.formatMessage({ id: 'ValidExampleShort' }) || 'e.g.'}:
              </Typography>
              <code style={{ fontSize: '13px', backgroundColor: '#fff', color: '#1890ff', padding: '2px 10px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 'bold', border: '1px solid #91caff' }}>
                {liveExample}
              </code>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ValidationRulesBuilder;
